import { useCallback, useEffect, useRef, useState } from 'react';
import { SESSION_SECURITY } from '../constants/session-config';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionSecurityConfig {
  /** Milliseconds of inactivity before the idle timeout fires. */
  idleTimeoutMs: number;
  /** Milliseconds from login until forced logout regardless of activity. */
  absoluteTimeoutMs: number;
  /** Milliseconds before idle timeout to show the warning. */
  warningBeforeMs: number;
  /** Called when the session expires (idle or absolute). */
  onLogout: () => Promise<void> | void;
  /** Called when the warning countdown starts. */
  onWarning: () => void;
  /** Called when the user dismisses the warning (extends session). */
  onWarningDismissed: () => void;
  /** Async check that the server-side session is still valid. */
  validateSession: () => Promise<boolean>;
  /** Set to false to disable all timers (e.g. on public pages). Default true. */
  enabled?: boolean;
}

export interface SessionSecurityReturn {
  /** Whether the "session about to expire" warning is currently visible. */
  isWarningVisible: boolean;
  /** User clicked "Rester connecté" — reset idle timer and hide warning. */
  extendSession: () => void;
  /** Seconds remaining in the warning countdown (0 when not warning). */
  remainingSeconds: number;
  /** Programmatic forced logout (e.g. from a "Se déconnecter" button). */
  forceLogout: () => void;
}

// ---------------------------------------------------------------------------
// Internal broadcast message types
// ---------------------------------------------------------------------------

interface LogoutMessage {
  type: 'SESSION_LOGOUT';
  reason: 'timeout' | 'user';
}

interface ActivityMessage {
  type: 'SESSION_ACTIVITY';
}

type BroadcastMessage = LogoutMessage | ActivityMessage;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isBroadcastMessage(data: unknown): data is BroadcastMessage {
  if (typeof data !== 'object' || data === null) return false;
  const msg = data as Record<string, unknown>;
  return msg.type === 'SESSION_LOGOUT' || msg.type === 'SESSION_ACTIVITY';
}

function createBroadcastChannel(
  name: string,
): BroadcastChannel | null {
  try {
    return new BroadcastChannel(name);
  } catch {
    // BroadcastChannel unavailable (Safari private mode, SSR, etc.)
    return null;
  }
}

function nowMs(): number {
  return Date.now();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useSessionSecurity(
  config: SessionSecurityConfig,
): SessionSecurityReturn {
  const {
    idleTimeoutMs,
    absoluteTimeoutMs,
    warningBeforeMs,
    onLogout,
    onWarning,
    onWarningDismissed,
    validateSession,
    enabled = true,
  } = config;

  // ---- State ----------------------------------------------------------
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // ---- Refs for timers & mutable state --------------------------------
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const absoluteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const lastActivityBroadcastRef = useRef(0);
  const lastValidationRef = useRef(0);
  const isLoggingOutRef = useRef(false);
  const mountedRef = useRef(true);

  // Store latest callbacks in refs to avoid stale closures
  const onLogoutRef = useRef(onLogout);
  const onWarningRef = useRef(onWarning);
  const onWarningDismissedRef = useRef(onWarningDismissed);
  const validateSessionRef = useRef(validateSession);

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  useEffect(() => {
    onWarningRef.current = onWarning;
  }, [onWarning]);

  useEffect(() => {
    onWarningDismissedRef.current = onWarningDismissed;
  }, [onWarningDismissed]);

  useEffect(() => {
    validateSessionRef.current = validateSession;
  }, [validateSession]);

  // ---- Core functions -------------------------------------------------

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current !== null) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const clearCountdownTimer = useCallback(() => {
    if (countdownTimerRef.current !== null) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  const clearAbsoluteTimer = useCallback(() => {
    if (absoluteTimerRef.current !== null) {
      clearTimeout(absoluteTimerRef.current);
      absoluteTimerRef.current = null;
    }
  }, []);

  /**
   * Broadcast a message to other tabs. Silently no-ops when the channel
   * is unavailable.
   */
  const broadcast = useCallback((message: BroadcastMessage) => {
    try {
      channelRef.current?.postMessage(message);
    } catch {
      // Channel may have been closed or become invalid
    }
  }, []);

  /**
   * Perform logout — called from idle timeout, absolute timeout, or
   * cross-tab broadcast. Guarded against double invocation.
   */
  const performLogout = useCallback(
    async (reason: 'timeout' | 'user') => {
      if (isLoggingOutRef.current) return;
      isLoggingOutRef.current = true;

      clearIdleTimer();
      clearCountdownTimer();
      clearAbsoluteTimer();

      broadcast({ type: 'SESSION_LOGOUT', reason });

      // Clean up storage
      try {
        sessionStorage.removeItem(SESSION_SECURITY.STORAGE_KEYS.LOGIN_TIMESTAMP);
        sessionStorage.removeItem(SESSION_SECURITY.STORAGE_KEYS.LAST_ACTIVITY);
      } catch {
        // sessionStorage may be unavailable
      }

      try {
        await onLogoutRef.current();
      } catch {
        // Logout callback errors should not prevent cleanup
      }
    },
    [broadcast, clearIdleTimer, clearCountdownTimer, clearAbsoluteTimer],
  );

  /**
   * Start the warning countdown. Updates `remainingSeconds` every second
   * via chained `setTimeout` calls (not `setInterval`).
   */
  const startCountdown = useCallback(
    (durationMs: number) => {
      const totalSeconds = Math.ceil(durationMs / 1000);
      setIsWarningVisible(true);
      setRemainingSeconds(totalSeconds);
      onWarningRef.current();

      let secondsLeft = totalSeconds;

      const tick = () => {
        if (!mountedRef.current) return;

        secondsLeft -= 1;

        if (secondsLeft <= 0) {
          setRemainingSeconds(0);
          void performLogout('timeout');
          return;
        }

        setRemainingSeconds(secondsLeft);
        countdownTimerRef.current = setTimeout(tick, 1000);
      };

      clearCountdownTimer();
      countdownTimerRef.current = setTimeout(tick, 1000);
    },
    [clearCountdownTimer, performLogout],
  );

  /**
   * Reset the idle timer. The timer fires `warningBeforeMs` before the
   * full idle timeout to trigger the warning.
   */
  const resetIdleTimer = useCallback(() => {
    clearIdleTimer();
    clearCountdownTimer();

    if (!mountedRef.current) return;

    setIsWarningVisible(false);
    setRemainingSeconds(0);

    const warningDelay = Math.max(idleTimeoutMs - warningBeforeMs, 0);

    idleTimerRef.current = setTimeout(() => {
      if (!mountedRef.current) return;
      startCountdown(warningBeforeMs);
    }, warningDelay);

    // Persist last activity for cross-tab visibility checks
    try {
      sessionStorage.setItem(
        SESSION_SECURITY.STORAGE_KEYS.LAST_ACTIVITY,
        String(nowMs()),
      );
    } catch {
      // sessionStorage may be unavailable
    }
  }, [
    idleTimeoutMs,
    warningBeforeMs,
    clearIdleTimer,
    clearCountdownTimer,
    startCountdown,
  ]);

  /**
   * Check if the absolute session timeout has been exceeded.
   * Returns true if the session is expired.
   */
  const isAbsoluteTimeoutExceeded = useCallback((): boolean => {
    try {
      const loginTs = sessionStorage.getItem(
        SESSION_SECURITY.STORAGE_KEYS.LOGIN_TIMESTAMP,
      );
      if (!loginTs) return false;
      return nowMs() - Number(loginTs) >= absoluteTimeoutMs;
    } catch {
      return false;
    }
  }, [absoluteTimeoutMs]);

  /**
   * Throttled session validation. Returns true if valid, false if expired.
   */
  const throttledValidation = useCallback(async (): Promise<boolean> => {
    const now = nowMs();
    if (now - lastValidationRef.current < SESSION_SECURITY.REVALIDATION_THROTTLE_MS) {
      return true; // Within throttle window — assume valid
    }
    lastValidationRef.current = now;

    try {
      return await validateSessionRef.current();
    } catch {
      // Network errors during validation — don't force logout
      return true;
    }
  }, []);

  // ---- Public API -----------------------------------------------------

  const extendSession = useCallback(() => {
    clearCountdownTimer();
    setIsWarningVisible(false);
    setRemainingSeconds(0);
    onWarningDismissedRef.current();
    resetIdleTimer();
    broadcast({ type: 'SESSION_ACTIVITY' });
  }, [clearCountdownTimer, resetIdleTimer, broadcast]);

  const forceLogout = useCallback(() => {
    void performLogout('user');
  }, [performLogout]);

  // ---- Main effect: wire up everything --------------------------------

  useEffect(() => {
    if (!enabled) return;

    mountedRef.current = true;
    isLoggingOutRef.current = false;

    // --- Login timestamp (set once per session) ---
    try {
      const existing = sessionStorage.getItem(
        SESSION_SECURITY.STORAGE_KEYS.LOGIN_TIMESTAMP,
      );
      if (!existing) {
        sessionStorage.setItem(
          SESSION_SECURITY.STORAGE_KEYS.LOGIN_TIMESTAMP,
          String(nowMs()),
        );
      }
    } catch {
      // sessionStorage unavailable
    }

    // --- Absolute timeout ---
    const setupAbsoluteTimeout = () => {
      clearAbsoluteTimer();
      try {
        const loginTs = sessionStorage.getItem(
          SESSION_SECURITY.STORAGE_KEYS.LOGIN_TIMESTAMP,
        );
        if (loginTs) {
          const elapsed = nowMs() - Number(loginTs);
          const remaining = absoluteTimeoutMs - elapsed;
          if (remaining <= 0) {
            void performLogout('timeout');
            return;
          }
          absoluteTimerRef.current = setTimeout(() => {
            void performLogout('timeout');
          }, remaining);
        }
      } catch {
        // sessionStorage unavailable
      }
    };

    setupAbsoluteTimeout();

    // --- Idle timer ---
    resetIdleTimer();

    // --- User activity listeners (throttled to 1 per second) ---
    let lastActivityTs = 0;

    const handleActivity = () => {
      if (isLoggingOutRef.current) return;

      const now = nowMs();
      if (now - lastActivityTs < 1000) return; // 1-second throttle
      lastActivityTs = now;

      // Check absolute timeout on each activity
      if (isAbsoluteTimeoutExceeded()) {
        void performLogout('timeout');
        return;
      }

      resetIdleTimer();

      // Broadcast activity to other tabs (throttled)
      if (
        now - lastActivityBroadcastRef.current >=
        1000
      ) {
        lastActivityBroadcastRef.current = now;
        broadcast({ type: 'SESSION_ACTIVITY' });
      }
    };

    const activityEvents: Array<keyof DocumentEventMap> = [
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    for (const event of activityEvents) {
      document.addEventListener(event, handleActivity, { passive: true });
    }

    // --- Visibility change (tab focus/blur) ---
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (isLoggingOutRef.current) return;

      // Check absolute timeout
      if (isAbsoluteTimeoutExceeded()) {
        void performLogout('timeout');
        return;
      }

      // Check if idle timeout passed while tab was hidden
      try {
        const lastActivity = sessionStorage.getItem(
          SESSION_SECURITY.STORAGE_KEYS.LAST_ACTIVITY,
        );
        if (lastActivity) {
          const elapsed = nowMs() - Number(lastActivity);
          if (elapsed >= idleTimeoutMs) {
            void performLogout('timeout');
            return;
          }
        }
      } catch {
        // sessionStorage unavailable
      }

      // Throttled re-validation against server
      void throttledValidation().then((isValid) => {
        if (!isValid && mountedRef.current) {
          void performLogout('timeout');
        }
      });

      // Reset idle timer since user is back
      resetIdleTimer();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // --- BroadcastChannel for multi-tab sync ---
    const channel = createBroadcastChannel(SESSION_SECURITY.BROADCAST_CHANNEL);
    channelRef.current = channel;

    const handleBroadcast = (event: MessageEvent) => {
      if (!isBroadcastMessage(event.data)) return;

      switch (event.data.type) {
        case 'SESSION_LOGOUT':
          void performLogout(event.data.reason);
          break;
        case 'SESSION_ACTIVITY':
          // Cross-tab activity resets idle timer locally
          if (!isLoggingOutRef.current) {
            resetIdleTimer();
          }
          break;
      }
    };

    channel?.addEventListener('message', handleBroadcast);

    // --- Cleanup ---
    return () => {
      mountedRef.current = false;
      clearIdleTimer();
      clearCountdownTimer();
      clearAbsoluteTimer();

      for (const event of activityEvents) {
        document.removeEventListener(event, handleActivity);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      channel?.removeEventListener('message', handleBroadcast);
      try {
        channel?.close();
      } catch {
        // Already closed
      }
      channelRef.current = null;
    };
  }, [
    enabled,
    idleTimeoutMs,
    absoluteTimeoutMs,
    resetIdleTimer,
    clearIdleTimer,
    clearCountdownTimer,
    clearAbsoluteTimer,
    performLogout,
    isAbsoluteTimeoutExceeded,
    throttledValidation,
    broadcast,
  ]);

  return {
    isWarningVisible,
    extendSession,
    remainingSeconds,
    forceLogout,
  };
}
